import { F } from '@/utils/intl';
import { Input, Popover } from 'antd';
import React, { useCallback, useRef } from 'react';
import {
  buildVariableSnippet,
  insertAtCursor,
} from './EmailBodyEditor/insertion';
import { EmailVariableGroup } from './EmailBodyEditor/types';
import VariableInsertButton from './VariableInsertButton';
import VariableOptionList from './VariableOptionList';
import { useVariableSuggestions } from './useVariableSuggestions';

import styles from './VariableTextField.less';

interface VariableTextFieldProps {
  value?: string;
  onChange?: (next: string) => void;
  /** Offered through the picker and the `{{` list; empty, the field is a plain input. */
  variableGroups?: EmailVariableGroup[];
  placeholder?: string;
  disabled?: boolean;
  size?: 'small' | 'middle';
  /** Grows as a textarea between these rows; omitted, a single-line input. */
  autoSize?: { minRows: number; maxRows: number };
}

function fieldElement(
  ref: React.RefObject<any>,
): HTMLInputElement | HTMLTextAreaElement | null {
  const current = ref.current;
  return current?.input ?? current?.resizableTextArea?.textArea ?? null;
}

/**
 * A text input with the variable picker and the `{{` autocomplete, for a saved
 * text's wording. Variables only: a `|trans` inside a translation is not rendered.
 */
const VariableTextField: React.FC<VariableTextFieldProps> = ({
  value = '',
  onChange,
  variableGroups,
  placeholder,
  disabled,
  size,
  autoSize,
}) => {
  const inputRef = useRef<any>(null);
  // Read while the field still has focus; the picker's popover takes it away.
  const selectionRef = useRef<{ start: number; end: number } | null>(null);

  const rememberSelection = useCallback(() => {
    const element = fieldElement(inputRef);
    if (!element) return;
    selectionRef.current = {
      start: element.selectionStart ?? 0,
      end: element.selectionEnd ?? 0,
    };
  }, []);

  const insertSnippet = useCallback(
    (snippet: string, span?: { start: number; end: number }) => {
      const selection = span ??
        selectionRef.current ?? { start: value.length, end: value.length };
      const { text, caret } = insertAtCursor(
        value,
        snippet,
        selection.start,
        selection.end,
      );
      onChange?.(text);
      selectionRef.current = { start: caret, end: caret };
      requestAnimationFrame(() => {
        const element = fieldElement(inputRef);
        if (!element) return;
        element.focus();
        element.setSelectionRange(caret, caret);
      });
    },
    [value, onChange],
  );

  const handleInsertVariable = useCallback(
    (namespace: string, name: string) =>
      insertSnippet(buildVariableSnippet(namespace, name)),
    [insertSnippet],
  );

  const offerVariables = !disabled && !!variableGroups?.length;

  const variables = useVariableSuggestions({
    variableGroups,
    enabled: offerVariables,
    onAccept: (option, trigger) =>
      insertSnippet(option.snippet, { start: trigger.start, end: trigger.end }),
  });

  const syncTrigger = useCallback(
    (element: HTMLInputElement | HTMLTextAreaElement) => {
      rememberSelection();
      const caret = element.selectionStart ?? element.value.length;
      variables.sync(element.value.slice(0, caret));
    },
    [variables, rememberSelection],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (!variables.handleKey(event.key)) return;
      // Enter would submit the form, Escape would close the drawer or popover.
      event.preventDefault();
      event.stopPropagation();
    },
    [variables],
  );

  const fieldProps = {
    ref: inputRef,
    className: styles.input,
    'data-variables': offerVariables ? '1' : undefined,
    value,
    placeholder,
    disabled,
    size,
    onChange: (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      onChange?.(event.target.value);
      syncTrigger(event.target);
    },
    onKeyDown: handleKeyDown,
    onSelect: (
      event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => syncTrigger(event.currentTarget),
    onClick: (
      event: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => syncTrigger(event.currentTarget),
    onBlur: () => {
      rememberSelection();
      variables.close();
    },
  };

  return (
    <div className={styles.field}>
      <Popover
        open={variables.open}
        trigger={[]}
        arrow={false}
        placement="bottomLeft"
        content={
          <div className={styles.suggestions}>
            <VariableOptionList
              options={variables.suggestions}
              activeIndex={variables.activeIndex}
              onPick={variables.accept}
            />
          </div>
        }
      >
        <div className={styles.inputWrap}>
          {autoSize ? (
            <Input.TextArea autoSize={autoSize} {...fieldProps} />
          ) : (
            <Input {...fieldProps} />
          )}
        </div>
      </Popover>

      {offerVariables && (
        <VariableInsertButton
          variableGroups={variableGroups}
          onInsert={handleInsertVariable}
          hint={
            <F
              id="pages.emailCenter.variableInsert.hintVariablesOnly"
              defaultMessage="Filled in per recipient when the email is sent. Typing {opener} offers the same list."
              values={{ opener: <code>{'{{'}</code> }}
            />
          }
        />
      )}
    </div>
  );
};

export default React.memo(VariableTextField);
